import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { createColumnHelper } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import { Pencil, XOctagon } from 'react-bootstrap-icons';
import Container from '../../layouts/Container';
import ListTable from '../../components/ListTable';
import AnchorTag from '../../components/AnchorTag';
import EditValidationStatusModal from '../../components/EditValidationStatusModal';
import {
  deleteValidationStatus,
  getValidationStatuses,
} from '../../services/project/validationStatusService';
import { useUsers } from '../../context/UserContext';

function ValidationStatuses() {
  const { projectId } = useParams();
  const { user, isAdmin } = useUsers();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const isAnalyst = user?.roles?.some(role => role?.name === 'GVE_Analyst');
  const canManage = Boolean(isAdmin && !isAnalyst);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await getValidationStatuses(projectId);
      const list = response?.statuses || response?.data || response || [];
      setItems(Array.isArray(list) ? list : []);
    } catch (error) {
      toast.error(error?.response?.data?.errorMessage || 'Failed to fetch validation statuses');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [projectId]);

  const handleAdd = () => {
    setSelectedItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const response = await deleteValidationStatus(id);
      toast.success(response?.message || 'Validation status deleted');
      fetchItems();
    } catch (error) {
      toast.error(error?.response?.data?.errorMessage || 'Failed to delete validation status');
    }
  };

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor(row => row.id, {
      id: 'id',
      header: 'ID',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor(row => row.name, {
      id: 'name',
      header: 'Name',
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor(row => row.description, {
      id: 'description',
      header: 'Description',
      cell: info => info.getValue() || '-',
    }),
    canManage && columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: info => {
        const item = info.row.original;
        return (
          <div className="d-flex gap-2">
            <AnchorTag onClick={() => handleEdit(item)} hoverText="Edit">
              <Pencil />
            </AnchorTag>
            <AnchorTag onClick={() => handleDelete(item.id)} hoverText="Delete">
              <XOctagon />
            </AnchorTag>
          </div>
        );
      },
    }),
  ].filter(Boolean);

  if (!canManage) {
    return (
      <Container>
        <h2 className="mb-3">Validation Status</h2>
        <p className="mb-0">You do not have access to this page.</p>
      </Container>
    );
  }

  return (
    <Container>
      <div className="d-flex align-items-center gap-2 mb-3">
        <h2 className="mb-0">Validation Status</h2>
        <Button onClick={handleAdd}>Add New</Button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <ListTable tableId="validation-status-list" data={items} columns={columns} />
      )}

      {showModal && (
        <EditValidationStatusModal
          show={showModal}
          status={selectedItem}
          projectId={projectId}
          onSaved={fetchItems}
          onClose={() => setShowModal(false)}
        />
      )}
    </Container>
  );
}

export default ValidationStatuses;
